import { FC, ReactNode } from 'react';
import { Alert as SpeculoAlert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { ShieldAlert } from 'lucide-react';
import { useOrganizationUsers } from '../../../hooks/graphql/users/useOrganizationUsers';

interface AdminLockOverlayProps {
  /** When true, the content is shown read-only with a lock banner on top. */
  locked: boolean;
  /** Sentence subject in the lock message. Default: "These settings". */
  subject?: string;
  children: ReactNode;
}

/** Normalize a raw GQL role onto the org-admin contract (mirrors useUser). */
const isOrgAdminRole = (role: string): boolean => {
  const r = role.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return r === 'org_admin' || r === 'admin';
};

/**
 * Wraps org settings so non-admins still SEE the current values but can't edit
 * them: a banner explains the restriction and surfaces the org admin's email,
 * and the controls below are disabled (read-only). We never hide or obscure
 * the settings — only lock editing.
 */
export const AdminLockOverlay: FC<AdminLockOverlayProps> = ({
  locked,
  subject = 'These settings',
  children,
}) => {
  const { users } = useOrganizationUsers();
  const adminEmails = locked
    ? users.filter((u) => isOrgAdminRole(u.role)).map((u) => u.email).filter(Boolean)
    : [];

  return (
    <div className="space-y-4">
      {locked && (
        <SpeculoAlert variant="warning">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            <span className="font-semibold">
              {subject} are managed by your organization admin.
            </span>{' '}
            You can view them here, but only an admin can make changes.
            {adminEmails.length > 0 && (
              <>
                {' '}
                Contact{' '}
                {adminEmails.map((email, i) => (
                  <span key={email}>
                    {i > 0 && ', '}
                    <a
                      href={`mailto:${email}`}
                      className="font-medium underline underline-offset-2"
                    >
                      {email}
                    </a>
                  </span>
                ))}
                {' '}to request a change.
              </>
            )}
          </AlertDescription>
        </SpeculoAlert>
      )}
      {/* Disabled (read-only) keeps values visible and legible; opacity-100
          overrides the browser's default dimming so the agent can still read
          every value. */}
      <fieldset disabled={locked} className="m-0 border-0 p-0 space-y-6 disabled:opacity-100">
        {children}
      </fieldset>
    </div>
  );
};

export default AdminLockOverlay;
